import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Play, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ParameterTypes } from '@/types'
import type { ChatCommandDescriptor, WorkflowTaskParameterSchema } from '@/types'

interface ChatCommandFormProps {
  command: ChatCommandDescriptor
  initialValues?: Record<string, string> | null
  isExecuting: boolean
  onExecute: (params: Record<string, string>) => void
  onCancel: () => void
}

function buildDefaultValues(parameters: WorkflowTaskParameterSchema[]): Record<string, string> {
  const defaults: Record<string, string> = {}
  for (const p of parameters) {
    defaults[p.name] = String(p.defaultValue ?? '')
  }
  return defaults
}

export function ChatCommandForm({ command, initialValues, isExecuting, onExecute, onCancel }: Readonly<ChatCommandFormProps>) {
  const defaultValues = {
    ...buildDefaultValues(command.parameters),
    ...(initialValues ?? {}),
  }

  const { control, handleSubmit, reset, formState: { errors } } = useForm<Record<string, string>>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [command.name, initialValues, reset])

  const onSubmit = (data: Record<string, string>) => {
    onExecute(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="border rounded-lg bg-card" data-testid="chat-command-form">
      <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
        <div>
          <span className="font-medium text-sm">{command.displayName}</span>
          {command.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{command.description}</p>
          )}
        </div>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onCancel} aria-label="Close form">
          <X className="h-3 w-3" />
        </Button>
      </div>

      {command.parameters.length > 0 && (
        <div className="p-3 space-y-3">
          {command.parameters.map((param) => (
            <CommandField key={param.name} param={param} control={control} error={errors[param.name]?.message} />
          ))}
        </div>
      )}

      <div className="px-3 py-2 border-t flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isExecuting}>
          {isExecuting ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <Play className="h-3 w-3 mr-1" />
              Execute
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

function CommandField({ param, control, error }: Readonly<{
  param: WorkflowTaskParameterSchema
  control: ReturnType<typeof useForm<Record<string, string>>>['control']
  error?: string
}>) {
  const id = `param-${param.name}`
  const label = param.displayName ?? param.name
  const rules = param.required ? { required: `${label} is required` } : undefined

  const inputType = (() => {
    if (param.type === ParameterTypes.Integer) return 'number'
    if (param.type === ParameterTypes.Secret) return 'password'
    return 'text'
  })()

  if (param.type === ParameterTypes.Boolean) {
    return (
      <Controller
        name={param.name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              id={id}
              data-testid={`param-input-${param.name}`}
              checked={field.value === 'true'}
              onCheckedChange={(checked) => field.onChange(checked ? 'true' : 'false')}
            />
            <span>
              {label}
              {param.required && <span className="text-red-500 ml-0.5">*</span>}
            </span>
            {param.description && (
              <span className="text-muted-foreground">— {param.description}</span>
            )}
          </label>
        )}
      />
    )
  }

  if (param.type === ParameterTypes.Select && param.options) {
    return (
      <div>
        <Label htmlFor={id} className="text-xs font-medium mb-1 block">
          {label}
          {param.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Controller
          name={param.name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger id={id} data-testid={`param-input-${param.name}`} className="text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {param.options!.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {error && <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>}
      </div>
    )
  }

  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium mb-1 block">
        {label}
        {param.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Controller
        name={param.name}
        control={control}
        rules={rules}
        render={({ field }) =>
          param.type === ParameterTypes.Textarea ? (
            <Textarea
              {...field}
              id={id}
              data-testid={`param-input-${param.name}`}
              placeholder={param.description}
              className="text-sm resize-none h-20"
            />
          ) : (
            <Input
              {...field}
              id={id}
              data-testid={`param-input-${param.name}`}
              type={inputType}
              placeholder={param.description}
              className="text-sm"
            />
          )
        }
      />
      {error && <p className="text-xs text-red-500 mt-1" role="alert">{error}</p>}
    </div>
  )
}
